import {
  AIBotPayload,
  AIBotServer,
  IAIBot,
  IAIBotScenarioData,
  normalizeAIBotChannelIds,
  normalizeAIBotScenarioData
} from 'shared/entities/smartbotAI';
import { FieldModel } from 'shared/models/form';
import { IRootStore } from 'shared/entities/store/rootStore';
import { apiUrls } from 'shared/entities/domain';
import { mapAITargetToTitle } from 'shared/entities/smartbotAIInfo';
import { renderTranslationString } from 'shared/entities/localization';
import { IChannelModel } from 'shared/entities/channels';

import { LoadingStageModel } from '../loadingStage';
import NewListModel from '../NewListModel';

import { AIEducationParamsModel } from './AIEducationParamsModel';

export const TEXTS_MAX_LENGTH = 5500;

export class AIBotModelBase implements IAIBot {
  readonly id: FieldModel<string>;

  readonly params: FieldModel<AIEducationParamsModel>;

  readonly rootStore: IRootStore;

  readonly scenarios: NewListModel<IAIBotScenarioData>;
  readonly channelIds: string[] | null = [];

  readonly editNameStage: LoadingStageModel = new LoadingStageModel();

  constructor({
    id,
    params,
    scenarios,
    channelIds,
    rootStore
  }: {
    id: string;
    params: AIEducationParamsModel;
    scenarios: IAIBotScenarioData[];
    channelIds: string[] | null;
    rootStore: IRootStore;
  }) {
    this.id = new FieldModel(id);
    this.params = new FieldModel(params);
    this.rootStore = rootStore;
    this.channelIds = channelIds;
    this.scenarios = NewListModel.fromJson(scenarios, (scenario) => ({
      key: scenario.id,
      entity: scenario
    }));
  }

  get name(): string {
    return this.params.value.name.value;
  }

  get intentTargetTitle(): string | null {
    const title = mapAITargetToTitle(this.params.value.target.value);

    return renderTranslationString(title, this.rootStore.translationsStore.t);
  }

  get channels(): IChannelModel[] {
    return this.channelListIds.reduce<IChannelModel[]>((acc, channelId) => {
      const channel =
        this.rootStore.channelsStore.channels.getEntity(channelId);

      if (!channel) {
        return acc;
      }

      acc.push(channel);
      return acc;
    }, []);
  }

  get channelListIds(): string[] {
    const channelIds = this.channelIds;
    if (channelIds === null) {
      return this.rootStore.channelsStore.channels.keys;
    }

    return channelIds.filter((channelId) =>
      this.rootStore.channelsStore.channels.keys.includes(channelId)
    );
  }

  editName = async (name: FieldModel<string>): Promise<BaseResponse> => {
    name.validate();

    if (this.editNameStage.isLoading || name.isError) {
      return {
        isError: true
      };
    }

    this.editNameStage.loading();
    const data = this.toJson();
    data.name = name.value;

    const response = await this.rootStore.networkStore.api(
      apiUrls.AI_BOTS_UPDATE,
      {
        method: 'POST',
        data
      }
    );

    if (!response.isError) {
      this.params.value.name.changeValue(name.value);
      this.editNameStage.success();
    } else {
      this.editNameStage.error();
    }
    return {
      isError: response.isError
    };
  };

  toJson(): AIBotPayload & { _id: string } {
    return {
      _id: this.id.value,
      ...this.params.value.toJson()
    };
  }

  static fromJson(raw: AIBotServer, rootStore: IRootStore) {
    return new AIBotModelBase({
      params: AIEducationParamsModel.fromJson(raw, rootStore.projectId),
      id: raw._id,
      scenarios: raw.extra?.scenarios?.map(normalizeAIBotScenarioData) ?? [],
      channelIds: normalizeAIBotChannelIds(raw.extra?.channel_ids),
      rootStore
    });
  }
}
